import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale } from 'lucide-react';

function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="card-glass mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl glass-strong flex items-center justify-center glow-blue">
              <Scale className="w-8 h-8 text-rebel-blue" />
            </div>
            <div>
              <h1 className="text-4xl font-black text-gradient">Terms of Service</h1>
              <p className="text-gray-400 mt-1">Last updated: January 21, 2026</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="card-glass space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">1. Agreement to Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              By accessing or using Rebel Prompts ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
              If you disagree with any part of these terms, you may not access the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">2. Description of Service</h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              Rebel Prompts is a prompt operating system that provides:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Prompt creation, organization, and management tools</li>
              <li>AI-assisted prompt generation using third-party AI services</li>
              <li>Public prompt discovery and sharing features</li>
              <li>Team collaboration and sharing</li>
              <li>Credit-based system for AI-powered features</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">3. User Accounts</h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              You are responsible for:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Maintaining the confidentiality of your account and password</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
              <li>Providing accurate and complete registration information</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-3">
              You must be at least 13 years old to use this Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">4. Credits and Payments</h2>
            <div className="space-y-3 text-gray-300 leading-relaxed">
              <p>
                <strong className="text-white">Credits:</strong> Certain AI features require credits. Credits are purchased 
                through subscription plans or one-time purchases.
              </p>
              <p>
                <strong className="text-white">Subscriptions:</strong> Subscription fees are billed in advance on a monthly 
                basis and are non-refundable except as required by law.
              </p>
              <p>
                <strong className="text-white">Unused Credits:</strong> Monthly subscription credits do not roll over to the 
                next billing period. Additional purchased credits may have different expiration terms.
              </p>
              <p>
                <strong className="text-white">Price Changes:</strong> We reserve the right to modify pricing with 30 days 
                notice to existing subscribers.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">5. Acceptable Use</h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              You agree NOT to:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any laws in your jurisdiction</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Scrape, data mine, or use automated tools without permission</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Create prompts that are illegal, harmful, or violate our content policy</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">6. Intellectual Property</h2>
            <div className="space-y-3 text-gray-300 leading-relaxed">
              <p>
                <strong className="text-white">Your Content:</strong> You retain ownership of prompts and content you create. 
                By making prompts public, you grant us a license to display and distribute that content within the platform.
              </p>
              <p>
                <strong className="text-white">Our Service:</strong> The Rebel Prompts platform, design, features, and 
                technology are owned by us and protected by copyright, trademark, and other laws.
              </p>
              <p>
                <strong className="text-white">AI-Generated Content:</strong> Content generated using AI features may be 
                subject to the terms of the underlying AI provider.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">7. Termination</h2>
            <p className="text-gray-300 leading-relaxed">
              We may terminate or suspend your account immediately, without prior notice, for conduct that we believe violates 
              these Terms or is harmful to other users, us, or third parties, or for any other reason.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">9. Disclaimers</h2>
            <p className="text-gray-300 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT 
              THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">10. Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed">
              IN NO EVENT SHALL REBEL PROMPTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE 
              DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">11. Changes to Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of material changes via email 
              or through the Service. Continued use after changes constitutes acceptance of new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-rebel-blue-light">12. Contact</h2>
            <p className="text-gray-300 leading-relaxed">
              For questions about these Terms, contact us at:{' '}
              <a href="mailto:Support@RebelPrompts.com" className="text-rebel-blue hover:underline">
                Support@RebelPrompts.com
              </a>
            </p>
          </section>

          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-sm text-gray-500 text-center">
              By using Rebel Prompts, you acknowledge that you have read and understood these Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
